/**
 * R-2's missing platform primitive (owner directive 2026-08-27, "an auth failure is a pause, not a
 * verdict"): the one injectable port through which a surface tells the upload lanes "a credential
 * event happened — reconsider everything you parked waiting for one".
 *
 * The review found the pieces already existed and were simply never connected: contact-admin ships
 * the whole mechanism for SSE (`eventsStore.ts` reconnects on `sessionLost -> false`, `auth.js`
 * exposes `onTokenChanged`) and zero upload code subscribes; contact-async has the only real redrive
 * (its "Sign in" button) and it was scoped to Sparky alone; contact-live models the park state
 * perfectly and renders no CTA. Every one of those is a wiring problem, and a wiring problem needs a
 * port, not another classifier.
 *
 * Two methods, deliberately no more:
 *  - `onAuthRecovered(cb)` a WAKE. It says only "try again now"; it carries no token, no identity and
 *    no verdict, because a lane that trusted a payload here would be latching a credential fact
 *    instead of re-deriving it (R-2 rule 4). Returns its own unsubscribe so a lane can be destroyed
 *    without leaking a listener into a long-lived auth store.
 *  - `isAuthCurrent()` the host's own verdict, used as the strongest evidence tier in
 *    `isAuthUploadFailure`. Async by contract: every real implementation has to ask an SDK, and a
 *    synchronous signature would have forced every host to cache the answer.
 *
 * The default is a NO-OP that reports the credential as current. That is the whole reason this file
 * can land before any app is wired: an unwired lane behaves exactly as it does today (auth failures
 * still pace on the R-0 slow tier and are still picked up by the startup requeue), and wiring a host
 * only ever makes recovery FASTER. `isAuthCurrent` defaults to `true` rather than `false` because
 * "we do not know" must not be allowed to declare a working surface auth-gated: the failure itself is
 * evidence, an absent port is not.
 */
export interface UploadAuthGate {
	/**
	 * Subscribe to this surface's auth-recovery signal — an interactive sign-in completing, a silent
	 * token refresh/rotation, a page load with a valid session, an org switch back. Returns an
	 * unsubscribe function. Implementations must tolerate being called more than once and must never
	 * throw from the callback's dispatch (see `createUploadAuthGate`).
	 */
	onAuthRecovered(callback: () => void): () => void;
	/** Does this surface hold a usable credential right now? */
	isAuthCurrent(): Promise<boolean>;
}

/**
 * The port every lane falls back to when no host has wired one. Exported (not just defaulted
 * internally) so a test or an app can assert "this surface is deliberately unwired" instead of
 * passing `undefined` and hoping.
 */
export const noopUploadAuthGate: UploadAuthGate = {
	onAuthRecovered: () => () => undefined,
	isAuthCurrent: async () => true
};

/** A gate plus the handle a host fires when its own auth-recovery signal arrives. */
export interface UploadAuthGateController {
	gate: UploadAuthGate;
	/** Fan the recovery signal out to every current subscriber. Never throws. */
	authRecovered(reason?: string): void;
	/** How many lanes are listening. Diagnostics only. */
	subscriberCount(): number;
}

export interface CreateUploadAuthGateOptions {
	/** The host's credential check. Defaults to "current", matching `noopUploadAuthGate`. */
	isAuthCurrent?: () => boolean | Promise<boolean>;
	/** Diagnostic label so the redrive logs name which surface fired. */
	label?: string;
	logger?: Pick<Console, "info" | "warn">;
}

/**
 * Build a gate for one surface. The host keeps the controller and calls `authRecovered()` from
 * whatever its credential event is; the lanes only ever see the `gate`.
 *
 * Every fan-out is logged and every subscriber is isolated (root `CLAUDE.md`, "Diagnostic logging
 * when fixing silent failures"): a redrive that reached no lane, or one lane whose handler threw and
 * silently skipped the rest, is precisely the invisible failure this port exists to end — and the
 * only observable symptom would be an upload that never resumed after a successful sign-in.
 */
export function createUploadAuthGate(options: CreateUploadAuthGateOptions = {}): UploadAuthGateController {
	const logger = options.logger ?? console;
	const label = options.label ?? "upload";
	const subscribers = new Set<() => void>();
	return {
		gate: {
			onAuthRecovered: (callback: () => void) => {
				subscribers.add(callback);
				return () => {
					subscribers.delete(callback);
				};
			},
			isAuthCurrent: async () => {
				if (!options.isAuthCurrent) return true;
				try {
					return (await options.isAuthCurrent()) !== false;
				} catch (error) {
					// A throwing credential check must not become "definitely not authenticated": that would
					// park healthy rows as auth-gated on a transient SDK fault. Report current and let the
					// failure itself classify.
					logger.warn(`[Upload] Auth gate credential check failed for ${label}; treating the credential as current`, error);
					return true;
				}
			}
		},
		authRecovered: (reason?: string) => {
			if (subscribers.size === 0) {
				logger.warn(`[Upload] Auth recovered for ${label} but no upload lane is subscribed; nothing was re-driven`, {
					reason: reason ?? null
				});
				return;
			}
			logger.info(`[Upload] Auth recovered for ${label}; re-driving auth-gated uploads`, {
				reason: reason ?? null,
				subscribers: subscribers.size
			});
			for (const subscriber of [...subscribers]) {
				try {
					subscriber();
				} catch (error) {
					logger.warn(`[Upload] Auth-recovery subscriber failed for ${label}; other lanes still ran`, error);
				}
			}
		},
		subscriberCount: () => subscribers.size
	};
}
